/**
 * ★★★ WU/U3 — A CÓPIA LOCAL DO FIRESTORE SE RENOVA SOZINHA.
 *
 * ## O caso que criou isto
 *
 * ⚠⚠ 29/set a 03/out: a **Jéssica** via *"Jessica Paterno (fora da função)"* no seletor de SDR,
 * o nome dela faltava no filtro do Funil, e o áudio não subia nem baixava — ★ **no computador
 * dela, com o login dela**, enquanto no do dono e no da Talita estava tudo normal.
 *
 * ★★★ A causa (medida, `docs/suporte-cache-navegador.md`): **a cópia persistente do Firestore
 * com um retrato velho de `usuarios`**. A primeira emissão do `onSnapshot` vem do disco, a lista
 * chegava incompleta, e `opcoesComGravado` injetava "(fora da função)" — ⚠ **o sufixo nunca
 * falou do cadastro dela: ele diz "este id não está na lista que a tela recebeu".**
 *
 * ★ Resolveu-se à mão, apagando os dados do site. ⚠⚠ **Cinco dias de trabalho atrapalhado por
 * algo que o sistema podia ter percebido sozinho.**
 *
 * ## ⚠⚠ Limpar a cópia É UM RELOAD — não uma operação de fundo
 *
 * ★ `clearIndexedDbPersistence` só vale **antes** de qualquer uso do Firestore, ou depois de
 * `terminate()` (que mata todo listener e não volta). ★★★ Por isso a limpeza acontece **na
 * inicialização**, antes de `initializeFirestore` ser usado — e por isso o gatilho por
 * inconsistência **marca** e espera a próxima carga, em vez de limpar na hora.
 *
 * ⚠ E com `persistentMultipleTabManager`, outra aba aberta faz a limpeza falhar com
 * `failed-precondition`. ★★ Isso **não pode travar o app**: segue com a cópia antiga e avisa.
 */
import { registrarErro } from '@/lib/erros';

/**
 * ⚠⚠ CHAVE LEGADA — **só existe para ser APAGADA** (07/out/2026, caminho (c)).
 *
 * ★ Ela guardava "a versão do build que criou esta cópia", e o gatilho era
 * `gravada !== versao → limpar`. ★★★ **Esse gatilho foi REMOVIDO**, porque ele apagava o
 * IndexedDB **compartilhado** e o SDK, ao ver o apagamento, **encerrava o cliente das abas já
 * abertas** (`setDatabaseDeletedListener → terminate()`, linha 26504 do SDK) — ⚠ em **todo
 * deploy**, para quem tinha uma aba esquecida aberta.
 *
 * ⚠ A chave é apagada do `localStorage` de quem a tenha. ★ Nada de IndexedDB aqui.
 */
export const CHAVE_VERSAO_LEGADA = (uid) => `odontosdr:versao-cache:${uid ?? 'anonimo'}`;
export const CHAVE_RENOVAR = (uid) => `odontosdr:renovar-cache:${uid ?? 'anonimo'}`;
export const CHAVE_ULTIMA = (uid) => `odontosdr:cache-renovado-em:${uid ?? 'anonimo'}`;

/**
 * ★★★ TRAVA ANTI-LAÇO: no máximo **uma** renovação por hora, por uid.
 *
 * ⚠⚠ O laço que isto evita: limpa → recarrega → a cópia nasce vazia → a equipe chega incompleta
 * de novo (porque o listener ainda não respondeu) → marca → limpa → recarrega. ★ Sem teto, a
 * tela entraria num ciclo de recarga que a pessoa não consegue interromper.
 *
 * ★★ Mesmo desenho do teto de `lib/erros.js` (20 registros por hora): **o remédio tem dose**.
 */
export const JANELA_MS = 60 * 60 * 1000;

/**
 * ★★★ WU-2/P5 — OS CÓDIGOS DO U3, e eles existem para o U3 deixar de ser INOBSERVÁVEL.
 *
 * ⚠⚠⚠ Medido na WU (`docs/wu-reincidencia-1610.md` §2.3 e §6.2): as duas chamadas de
 * `registrarErro` daqui passavam **dois argumentos posicionais** a uma função que recebe
 * **um objeto** — então, se chegassem a disparar, gravariam `codigo: 'sem-codigo'`, mensagem
 * **vazia** e **sem assunto**. ★ E em produção há **zero** registros com `sem-codigo`, nas
 * duas clínicas, em toda a história de `erros`.
 *
 * ★★ Com código próprio, a próxima ocorrência vira medição: dá para perguntar ao banco "o U3
 * agiu alguma vez?" — hoje não dá, e por isso "ele não disparou" se afirma pelo código, não
 * pelo banco.
 */
export const CODIGO_INCOERENCIA_PERSISTENTE = 'copia-local:incoerencia-persistente';
export const CODIGO_FALHA_AO_RENOVAR = 'copia-local:falha-ao-renovar';

const deposito = () => {
  try {
    // ⚠ Só TOCAR em `globalThis.localStorage` já lança em navegador com cookies bloqueados.
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
};

const ler = (chave) => {
  try {
    return deposito()?.getItem(chave) ?? null;
  } catch {
    return null;
  }
};
const gravar = (chave, valor) => {
  try {
    deposito()?.setItem(chave, valor);
  } catch {
    // ⚠ Sem depósito o U3 simplesmente não age. Não é erro: é um navegador que não guarda.
  }
};
const apagar = (chave) => {
  try {
    deposito()?.removeItem(chave);
  } catch { /* idem */ }
};

/** ★ Já renovou dentro da janela? É a trava. */
export function renovouRecentemente(uid, agora = Date.now()) {
  const quando = Number(ler(CHAVE_ULTIMA(uid)));
  return Number.isFinite(quando) && quando > 0 && agora - quando < JANELA_MS;
}

/** ★★ O sinal da Jéssica: a equipe chegou e o PRÓPRIO usuário não está nela. */
export function equipeIncoerente(equipe, uid) {
  if (!uid || !Array.isArray(equipe) || equipe.length === 0) return false;
  return !equipe.some((pessoa) => pessoa?.id === uid);
}

/**
 * Marca que a cópia precisa renovar na próxima carga.
 *
 * ⚠⚠ **Não limpa agora**: limpar exige estar antes do primeiro uso do Firestore, e aqui já
 * estamos com listeners abertos. ★ Devolve `true` se marcou — é o que acende a faixa.
 */
export function pedirRenovacao(uid, agora = Date.now()) {
  if (renovouRecentemente(uid, agora)) {
    /**
     * ★★★ Segunda vez na mesma hora: **para de oferecer** e registra.
     * ⚠ Se limpar a cópia não resolveu a incoerência, o problema **não é a cópia** — e insistir
     * esconderia a causa real atrás de recargas.
     */
    // ★★★ WU-2/P5 — FORMA DE OBJETO. ⚠ Antes eram dois argumentos posicionais, e o segundo
    // era simplesmente IGNORADO pela assinatura de `registrarErro`.
    registrarErro({
      origem: 'service',
      codigo: CODIGO_INCOERENCIA_PERSISTENTE,
      assunto: 'a cópia local dos dados',
      mensagem: 'Cópia local renovada sem resolver a incoerência da equipe',
    });
    return false;
  }
  gravar(CHAVE_RENOVAR(uid), '1');
  return true;
}

export const renovacaoPedida = (uid) => ler(CHAVE_RENOVAR(uid)) === '1';

/**
 * ★★★ RODA NA INICIALIZAÇÃO, ANTES DE QUALQUER USO DO FIRESTORE.
 *
 * Limpa a cópia **SÓ pelo sinal de incoerência** — a equipe tendo chegado sem o próprio usuário.
 *
 * ## ⚠⚠ O GATILHO POR VERSÃO FOI REMOVIDO EM 07/out/2026, e é a lição da peça
 *
 * ★ Ele era `versão gravada ≠ hash do bundle → limpar`, e parecia inofensivo.
 * ★★★ **Medido** (`docs/wu-falha-duas-abas.md`): `clearIndexedDbPersistence` apaga o IndexedDB
 * **compartilhado**, e o SDK, ao ver esse apagamento, **chama `terminate()` no cliente das abas
 * que já estavam abertas** (linha 26504 de `common-*.rn.js`). ⚠⚠ Resultado medido no canário: a
 * aba aberta ficou com **os seis listeners mortos**, e **nem o registro do erro saiu** — porque
 * o registrador grava pelo mesmo `db`.
 *
 * ★★★ E repetia em **TODO deploy**: ⚠ a WU foi feita para quem deixa o sistema aberto o dia
 * inteiro, e esse gatilho **quebrava exatamente essa pessoa**.
 *
 * ⚠ **Nunca houve prova de que trocar de versão torne a cópia errada.** O caso da Jéssica foi
 * **retrato velho**, não incompatibilidade de formato — ★ e é esse caso que o sinal cobre.
 *
 * @returns {{limpou: boolean, motivo: string|null, outraAba: boolean}}
 */
export async function renovarCopiaSeNecessario({
  uid,
  limpar,
  agora = Date.now(),
} = {}) {
  // ★ Higiene: a chave do gatilho removido sai do `localStorage` de quem a tenha.
  apagar(CHAVE_VERSAO_LEGADA(uid));

  if (!renovacaoPedida(uid)) return { limpou: false, motivo: null, outraAba: false };

  try {
    await limpar();
  } catch (erro) {
    if (erro?.code === 'failed-precondition') {
      /**
       * ★★★ OUTRA ABA ABERTA. ⚠⚠ Não trava o app: segue com a cópia antiga.
       * ★ E **a marca FICA** — senão a renovação ficaria "feita" sem ter acontecido, e nunca
       * mais seria tentada.
       */
      return { limpou: false, motivo: 'outra-aba', outraAba: true };
    }
    // ⚠ Qualquer outra falha: registra e segue. Cache que não limpou não pode derrubar o app.
    /**
     * ★★★ WU-2/P5 — idem, e o `codigo` é o NOSSO, não o do erro.
     *
     * ⚠ O `code` do SDK (quando existe) vai para a mensagem: ★ aqui o que o dono precisa achar
     * na Saúde é *"a renovação da cópia local falhou"*, e isso não se agrupa por um código de
     * IndexedDB que varia com o navegador.
     */
    registrarErro({
      origem: 'service',
      codigo: CODIGO_FALHA_AO_RENOVAR,
      assunto: 'a renovação dos dados locais',
      mensagem: `${erro?.code ?? 'sem code'}: ${erro?.message ?? String(erro ?? '')}`,
    });
    return { limpou: false, motivo: 'erro', outraAba: false };
  }

  /**
   * ★ Apagar a marca e carimbar a hora é o que fecha o ciclo: ⚠ sem a marca, a próxima carga não
   * limparia de novo; ★★ sem o carimbo, a trava de 1 por hora não teria contra o que comparar.
   */
  apagar(CHAVE_RENOVAR(uid));
  gravar(CHAVE_ULTIMA(uid), String(agora));
  return { limpou: true, motivo: 'sinal', outraAba: false };
}
